/**
 * 콜아웃 문법 분리 (순수 함수).
 *
 * 규약(`docs/manual/README.md`)의 `> [!TIP]` / `> [!WARNING]` / `> [!TRAP]` / `> [!NOTE]`
 * 블록을 일반 마크다운 조각과 분리한다. 마커 줄은 본문에서 제거된다.
 *
 * ⚠️ **이 목록에 없는 변종은 조용히 통과한다** — 매칭이 안 되면 일반 markdown 조각으로
 * 흘러가, 화면에 `[!NOTE]` 같은 날문자가 blockquote 첫 줄로 그대로 보인다(렌더가 깨지지
 * 않으므로 아무도 못 본다). 실제로 `NOTE` 23건·`IMPORTANT` 3건이 그 상태로 쌓여 있었고
 * (2026-10-04 발견) `NOTE`는 여기 추가해 살리고 `IMPORTANT`는 WARNING으로 흡수했다.
 * 재발 차단은 `manualCallouts.test.ts`의 「지원 밖 콜아웃 변종이 문서에 0건」이 맡는다 —
 * 변종을 더하려면 **그 가드의 분모(이 타입)와 ManualDoc의 CALLOUT_STYLE을 함께** 늘린다.
 */

export type CalloutVariant = 'TIP' | 'WARNING' | 'TRAP' | 'NOTE';

export type ManualSegment =
  | { kind: 'markdown'; content: string }
  | { kind: 'callout'; variant: CalloutVariant; content: string };

/**
 * 지원 변종 — 가드가 문서를 스캔할 때 쓰는 분모.
 *
 * ⚠️ 아래 `CALLOUT_MARKER`는 **일부러 리터럴**이다(이 배열로 `new RegExp`를 조립하지 말 것 —
 * 데이터로 만든 정규식은 쓴 그대로가 아니고, 조립하면 두 사본이 **서로 일치하며 둘 다 틀릴**
 * 수 있어 가드가 공허해진다). 둘의 드리프트는 `manualCallouts.test.ts`가 정규식 소스를 읽어
 * 대조한다.
 */
export const CALLOUT_VARIANTS: readonly CalloutVariant[] = ['TIP', 'WARNING', 'TRAP', 'NOTE'];

const CALLOUT_MARKER = /^>[ \t]*\[!(TIP|WARNING|TRAP|NOTE)\][ \t]*(.*)$/i;

/** 가드가 리터럴 정규식 ↔ CALLOUT_VARIANTS 일치를 대조할 수 있도록 노출. */
export const CALLOUT_MARKER_SOURCE = CALLOUT_MARKER.source;

/**
 * 본문을 마크다운 조각과 콜아웃 블록으로 나눈다.
 *
 * 마커 줄에 이어지는 인용(`>`) 줄이 콜아웃 내용이 되고, 코드펜스 안의 `>`는 건드리지 않는다.
 */
export function splitCallouts(body: string): ManualSegment[] {
  const lines = body.split(/\r?\n/);
  const segments: ManualSegment[] = [];
  let buffer: string[] = [];
  let inFence = false;

  const flush = () => {
    if (buffer.length === 0) return;
    const content = buffer.join('\n');
    if (content.trim()) segments.push({ kind: 'markdown', content });
    buffer = [];
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    if (/^[ \t]*(```|~~~)/.test(line)) {
      inFence = !inFence;
      buffer.push(line);
      continue;
    }

    const marker = inFence ? null : CALLOUT_MARKER.exec(line);
    if (!marker) {
      buffer.push(line);
      continue;
    }

    flush();

    const variant = marker[1].toUpperCase() as CalloutVariant;
    const inner: string[] = [];
    if (marker[2].trim()) inner.push(marker[2].trim());

    let j = i + 1;
    while (j < lines.length && /^[ \t]*>/.test(lines[j])) {
      inner.push(lines[j].replace(/^[ \t]*>[ \t]?/, ''));
      j += 1;
    }
    i = j - 1;

    segments.push({ kind: 'callout', variant, content: inner.join('\n') });
  }

  flush();
  return segments;
}
